import React, { useState, useMemo } from "react";
import useSWR from "swr";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Spinner, Empty } from "@/components/Atoms";
import { fmtRel } from "@/lib/format";
import { Upload, X, Trash2, Download, Puzzle, Globe2, Building2, Cloud } from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmtBytes = (n) => {
    if (!n && n !== 0) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const SCOPE_META = {
    global: { label: "Global", icon: Globe2, cls: "bg-cm-brand/15 text-cm-brand border-cm-brand/30" },
    reseller: { label: "Reseller", icon: Building2, cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
    cloud: { label: "Tenant", icon: Cloud, cls: "bg-teal-500/15 text-teal-300 border-teal-500/30" },
};

function ScopeBadge({ scope }) {
    const m = SCOPE_META[scope] || SCOPE_META.global;
    const Icon = m.icon;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${m.cls}`}>
            <Icon size={11} /> {m.label}
        </span>
    );
}

export default function CustomAppsPage() {
    const { data, mutate, isLoading } = useSWR("/api/admin/custom-apps", fetcher);
    const { data: resellers } = useSWR("/api/admin/resellers", fetcher);
    const { data: cloudsResp } = useSWR("/api/admin/clouds?limit=300", fetcher);
    const clouds = useMemo(
        () => (Array.isArray(cloudsResp) ? cloudsResp : cloudsResp?.items || []),
        [cloudsResp]
    );

    const resellerName = useMemo(() => {
        const m = {};
        (resellers || []).forEach((r) => (m[r.id] = r.name));
        return m;
    }, [resellers]);
    const cloudName = useMemo(() => {
        const m = {};
        clouds.forEach((c) => (m[c.id] = c.subdomain || c.primary_domain || c.fallback_hostname || c.id));
        return m;
    }, [clouds]);

    const [showForm, setShowForm] = useState(false);
    const [scope, setScope] = useState("global");
    const [resellerId, setResellerId] = useState("");
    const [cloudId, setCloudId] = useState("");
    const [ncMin, setNcMin] = useState("");
    const [ncMax, setNcMax] = useState("");
    const [file, setFile] = useState(null);
    const [err, setErr] = useState(null);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        if (!file) {
            setErr("Please select a .tar.gz file.");
            return;
        }
        if (scope === "global" &&
            !window.confirm("Global apps will be installed on ALL clouds of all resellers at the next reconcile. Continue?")) {
            return;
        }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("scope", scope);
        if (scope === "reseller") fd.append("reseller_id", resellerId);
        if (scope === "cloud") fd.append("cloud_id", cloudId);
        if (ncMin !== "") fd.append("nc_min", ncMin);
        if (ncMax !== "") fd.append("nc_max", ncMax);
        setBusy(true);
        try {
            await api.post("/api/admin/custom-apps", fd, {
                headers: { "Content-Type": undefined },
            });
            setFile(null);
            setShowForm(false);
            setScope("global");
            setResellerId("");
            setCloudId("");
            setNcMin("");
            setNcMax("");
            await mutate();
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(false);
    };

    const remove = async (id) => {
        if (!window.confirm("Remove this custom app? It will be uninstalled from all affected clouds at the next agent reconcile.")) return;
        try {
            await api.delete(`/api/admin/custom-apps/${id}`);
            await mutate();
        } catch (e) {
            alert(formatApiError(e));
        }
    };

    const target = (a) =>
        a.scope === "reseller"
            ? resellerName[a.reseller_id] || a.reseller_id
            : a.scope === "cloud"
            ? cloudName[a.cloud_id] || a.cloud_id
            : "all clouds";

    return (
        <div data-testid="custom-apps-page">
            <PageHeader
                testid="custom-apps-header"
                title="Custom Apps"
                subtitle="Central registry for non-App-Store apps. Bundles are stored durably and pulled by every tenant VM — globally, per reseller, or per tenant."
                action={
                    <button
                        data-testid="custom-apps-upload-toggle"
                        className="cm-btn-primary flex items-center gap-2"
                        onClick={() => setShowForm((v) => !v)}
                    >
                        {showForm ? <X size={14} /> : <Upload size={14} />}
                        {showForm ? "Cancel" : "Upload app"}
                    </button>
                }
            />

            {showForm && (
                <form
                    onSubmit={submit}
                    className="m-4 sm:m-6 cm-card p-5 space-y-4 max-w-2xl cm-fade-in"
                    data-testid="custom-apps-form"
                >
                    <div>
                        <label className="cm-label">App bundle (.tar.gz with <span className="font-mono">&lt;app_id&gt;/appinfo/info.xml</span>)</label>
                        <input
                            data-testid="custom-apps-file"
                            type="file"
                            accept=".gz,.tgz,application/gzip"
                            className="cm-input mt-1 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-[#CCFF00] file:text-white file:text-xs"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                        />
                    </div>

                    <div>
                        <label className="cm-label">Scope</label>
                        <div className="flex gap-2 mt-1">
                            {["global", "reseller", "cloud"].map((s) => {
                                const m = SCOPE_META[s];
                                const Icon = m.icon;
                                return (
                                    <button
                                        type="button"
                                        key={s}
                                        data-testid={`custom-apps-scope-${s}`}
                                        onClick={() => setScope(s)}
                                        className={[
                                            "flex items-center gap-1.5 px-3 py-1.5 rounded border text-xs transition-colors",
                                            scope === s
                                                ? "border-[#CCFF00] bg-zinc-900 text-zinc-50"
                                                : "border-zinc-700 text-zinc-400 hover:text-zinc-100",
                                        ].join(" ")}
                                    >
                                        <Icon size={13} /> {m.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {scope === "reseller" && (
                        <div>
                            <label className="cm-label">Reseller</label>
                            <select
                                data-testid="custom-apps-reseller-select"
                                className="cm-input mt-1"
                                value={resellerId}
                                onChange={(e) => setResellerId(e.target.value)}
                                required
                            >
                                <option value="">— select reseller —</option>
                                {(resellers || []).map((r) => (
                                    <option key={r.id} value={r.id}>{r.name}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {scope === "cloud" && (
                        <div>
                            <label className="cm-label">Tenant / Cloud</label>
                            <select
                                data-testid="custom-apps-cloud-select"
                                className="cm-input mt-1"
                                value={cloudId}
                                onChange={(e) => setCloudId(e.target.value)}
                                required
                            >
                                <option value="">— select cloud —</option>
                                {clouds.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.subdomain || c.primary_domain || c.fallback_hostname || c.id}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div>
                        <label className="cm-label">
                            NC version compatibility <span className="text-zinc-500">(optional — otherwise from info.xml)</span>
                        </label>
                        <div className="flex items-center gap-2 mt-1">
                            <input
                                data-testid="custom-apps-nc-min"
                                type="number"
                                min="0"
                                placeholder="min (e.g. 33)"
                                className="cm-input w-32"
                                value={ncMin}
                                onChange={(e) => setNcMin(e.target.value)}
                            />
                            <span className="text-zinc-500">–</span>
                            <input
                                data-testid="custom-apps-nc-max"
                                type="number"
                                min="0"
                                placeholder="max (e.g. 34)"
                                className="cm-input w-32"
                                value={ncMax}
                                onChange={(e) => setNcMax(e.target.value)}
                            />
                        </div>
                        <p className="text-xs text-zinc-500 mt-1">
                            Upload the same app multiple times with different ranges (e.g. 33–33 and 34–34) — on upgrade each cloud automatically pulls the matching variant.
                        </p>
                    </div>

                    {err && <div className="text-xs text-cm-danger" data-testid="custom-apps-form-error">{err}</div>}
                    <button
                        type="submit"
                        disabled={busy}
                        className="cm-btn-primary"
                        data-testid="custom-apps-form-submit"
                    >
                        {busy ? "Uploading…" : "Upload & register"}
                    </button>
                </form>
            )}

            <div className="p-4 sm:p-6 pt-0">
                {isLoading ? (
                    <Spinner />
                ) : !data?.length ? (
                    <Empty
                        title="No custom apps yet"
                        hint="Upload a .tar.gz bundle — globally, per reseller, or per tenant."
                        testid="custom-apps-empty"
                    />
                ) : (
                    <div className="cm-card overflow-hidden">
                        <div className="cm-table-wrap">
                            <table className="cm-table">
                                <thead>
                                    <tr>
                                        <th className="cm-th">App</th>
                                        <th className="cm-th">Scope</th>
                                        <th className="cm-th">Target</th>
                                        <th className="cm-th">Version</th>
                                        <th className="cm-th">NC</th>
                                        <th className="cm-th">Size</th>
                                        <th className="cm-th">Uploaded</th>
                                        <th className="cm-th"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.map((a) => (
                                        <tr key={a.id} className="cm-tr" data-testid={`custom-app-row-${a.id}`}>
                                            <td className="cm-td">
                                                <div className="flex items-center gap-2">
                                                    <Puzzle size={14} className="text-zinc-500" />
                                                    <div>
                                                        <div className="text-zinc-100">{a.name || a.app_id}</div>
                                                        <div className="font-mono text-xs text-zinc-500">{a.app_id}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="cm-td"><ScopeBadge scope={a.scope} /></td>
                                            <td className="cm-td text-xs text-zinc-300">{target(a)}</td>
                                            <td className="cm-td font-mono text-xs">{a.version || "—"}</td>
                                            <td className="cm-td font-mono text-xs text-zinc-400">
                                                {a.nc_min || a.nc_max ? `${a.nc_min ?? "*"}–${a.nc_max ?? "*"}` : "alle"}
                                            </td>
                                            <td className="cm-td font-mono text-xs text-zinc-400">{fmtBytes(a.size)}</td>
                                            <td className="cm-td text-xs text-zinc-500">{fmtRel(a.created_at)}</td>
                                            <td className="cm-td">
                                                <div className="flex items-center gap-1 justify-end">
                                                    <a
                                                        data-testid={`custom-app-download-${a.id}`}
                                                        href={`${BACKEND}/api/admin/custom-apps/${a.id}/download`}
                                                        className="cm-btn-ghost p-1.5"
                                                        title="Download"
                                                    >
                                                        <Download size={14} />
                                                    </a>
                                                    <button
                                                        data-testid={`custom-app-delete-${a.id}`}
                                                        onClick={() => remove(a.id)}
                                                        className="cm-btn-ghost p-1.5 text-cm-danger"
                                                        title="Remove"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
